import CodeMirror from "codemirror/lib/codemirror";
import {
  CodeMirrorWidget,
  FormFieldWidget,
  FormulaFunctionWidget,
  ICompletion,
  MentionWidget,
  WidgetType
} from "./codeMirrorWidget";
import {Hash} from "../interfaces/hashInterfaces";
import {IDynamicReferencedThing} from "../interfaces/dynamicFieldsInterfaces";
import EventHandler = JQuery.EventHandler;
import ClickEvent = JQuery.ClickEvent;
import DynamicFieldsHandler from "~/models/dynamicFields/handlers/dynamicFieldsHandler";
import Formula from "~/models/formula";


export interface IReplaceContentConfig<T extends string>{
  referencedThingLookupFunction: (content: T, fieldsHandler?: DynamicFieldsHandler) => {},
  widgetListener: EventHandler<HTMLElement, ClickEvent>,
  fieldsHandler?: DynamicFieldsHandler
}

export default class CodeMirrorEditor {
  codeMirror: CodeMirror;
  widgets: {[key: string]: CodeMirrorWidget} = {};
  clientIdRegExp: RegExp = Formula.DYNAMIC_ID_REGEXP
  protected _currentSelectedChip: JQuery;

  constructor(selectorClass: string, configs, codeMirrorAble: {editor: CodeMirrorEditor}) {
    this.codeMirror = CodeMirror.fromTextArea(document.querySelector(selectorClass), configs);
    if (codeMirrorAble) codeMirrorAble.editor = this;
    this.registerEvents();
  }

  findMark(markId): any {
    return this.codeMirror.getAllMarks().find(m => {
      return m.id === markId;
    });
  }

  async addWidget(type: WidgetType, opts: {name: string} | Partial<IDynamicReferencedThing>): Promise<CodeMirrorWidget> {
    let widget: CodeMirrorWidget;
    switch (type) {
      case WidgetType.FormulaFunctionWidget:
        widget = new FormulaFunctionWidget(this, opts as Hash);
        break;
      case WidgetType.FormFieldWidget:
        widget = new FormFieldWidget(this, opts as IDynamicReferencedThing);
        break;
      case WidgetType.MentionWidget:
        widget = new MentionWidget(this, opts as ICompletion);
        break;
      default:
        widget = null;
    }
    if (widget) {
      this.widgets[widget.guid] = widget;
      return new Promise((resolve, reject) => {
        resolve(widget);
      });
    }
  }

  async replaceWidget(newWidgetType: WidgetType, newWidgetOpts): Promise<CodeMirrorWidget> {
    const $chip = this.currentSelectedChip;
    if ($chip) {
      const markId = $chip.data('mark-id');
      const textMarker = this.findMark(markId);
      if (textMarker) {
        const {from, to} = textMarker.find();
        textMarker.clear();
        delete this.widgets[$chip.data('guid')];
        this.codeMirror.replaceRange('', from, to);
        this.codeMirror.setCursor(from);
      }
    }
    return new Promise(async (resolve, reject) => {
      resolve(await this.addWidget(newWidgetType, newWidgetOpts));
      this.currentSelectedChip = null;
    })
  }


  replaceContent(contentConfig: IReplaceContentConfig<string>): void {
    const cursorLocation = this.codeMirror.getCursor();
    let {referencedThingLookupFunction, widgetListener, fieldsHandler} = contentConfig;
    const replacementText: string = this.codeMirror.getValue();
    if (replacementText) {
      const clientIdMatches: Set<string> = new Set(replacementText.match(this.clientIdRegExp) || []);
      if (clientIdMatches.size > 0) {
        clientIdMatches.forEach(cId => {
          const searchCursor = this.codeMirror.getSearchCursor(cId);
          while (searchCursor.findNext()) {
            const referencedThing: Partial<IDynamicReferencedThing> = referencedThingLookupFunction(cId, fieldsHandler);
            if (referencedThing) {
              this.codeMirror.replaceRange('', searchCursor.pos.from, searchCursor.pos.to);
              this.codeMirror.setCursor(searchCursor.pos.from);
              this.addWidget(WidgetType.FormFieldWidget, referencedThing).then(function(widget) {
                widget.marks.forEach(mark => {
                  mark.replacedWith.addEventListener('click', widgetListener);
                });
              });
            }
          }
        });
      }
      this.codeMirror.setCursor(cursorLocation)
    }
  }

  removeSelectedPills(): void {
    const selectedPills: JQuery = $(this.wrapperElement).find('[data-mark-id].pill.formula-pill.active');
    const widgetRefs: Array<DOMStringMap> = selectedPills.toArray().map((el) => {
      return el.dataset
    });

    let markToRemove;
    widgetRefs.forEach(data => {
      markToRemove = this.codeMirror.getAllMarks().find(m => {
        return m.id === parseInt(data.markId, 10);
      });
      const {from, to} = markToRemove.find();
      markToRemove.clear();
      delete this.widgets[data.guid];
      this.codeMirror.replaceRange('', from, to);
      this.codeMirror.setCursor(from);
    });
    this.codeMirror.focus();
    $(this.wrapperElement).trigger('chip:cleared');
    $(document).trigger('cTooltip:clear');
  }

  registerEvents(): void {
    $(document).on('keyup.removePills', (e) => {
      if (e.key && ['Backspace', 'Delete'].includes(e.key)) {
        if (this.selectedChips.length > 0) {
          this.removeSelectedPills();
        }
      }
    });

    $(this.wrapperElement).on('chip:selected', "VAR",  e => {
      if (e.target.tagName === 'VAR') this.currentSelectedChip = $(e.target)
    })
  }

  setInputReadListener(cb: (cm: CodeMirror, ch: any) => void): void {
    this.codeMirror.on('inputRead', cb);
  }

  get selectedChips() {
    return $(this.wrapperElement).find('[data-mark-id].pill.formula-pill.active').toArray();
  }

  get textarea(): HTMLElement {
    return this.codeMirror.getTextArea();
  }

  /**
   * the proxy DOM element CodeMirror creates
   * to manage all its functionality
   * This is the element that will be emitting events
   */
  get wrapperElement(): HTMLElement {
    return this.codeMirror.getWrapperElement();
  }

  /**
   * Chip (formerly Pill) management
   */
  get currentSelectedChip(): JQuery<HTMLElement> {
    return this._currentSelectedChip
  }

  set currentSelectedChip($element: JQuery<HTMLElement>) {
    if ($element) {
      $element.toggleClass('active');
      if ($element.hasClass('active')) {
        this._currentSelectedChip = $element;
        return;
      }
    }
    this._currentSelectedChip = null;
  }

  removeEvents() {
    const $document = $(document);
    $document.off('keyup.removePills');
    $document.off('chip:selected');
  }
}
